import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Div,
  DivProps,
  Heading,
  ImageProps,
  P,
} from '@kizi/core-components';
import {
  ModalWithAccordion,
  ModalWithAccordionProps,
} from './modals/ModalWithAccordion';
import { Desktop, Mobile } from '../utils/breakpoint';
import { IconV2 as Icon, Anchor } from '@kizi/shared-components';
import { Window } from './Window';
import { isMobile } from '../utils/global';
declare global {
  interface Window {
    FrontChat: any;
  }
}

export interface Contact {
  type: string;
  icon: string;
  label: string;
  prelabel?: string;
  href?: string;
  text?: string;
  extraText?: string;
  afterText?: string;
  responseTime?: string;
}

export interface Operation {
  isOperating: boolean;
  closingHoursMessage: string;
}
export interface HelpModalsProps {
  faq?: ModalWithAccordionProps;
  howWeCompare?: ModalWithAccordionProps;
}
export interface InnerHelpProps extends DivProps {
  title: string;
  imageUrl?: string;
  imageProps?: ImageProps;
  subtitle: string;
  operation?: Operation;
  contacts: Contact[];
  modals?: HelpModalsProps;
  refForModal?: React.Ref<HTMLDivElement> | null;
  showIcon?: boolean;
  imageStyles?: any;
}

export interface OperationalProps {
  isOperating: boolean;
}

export const Operational: React.FC<OperationalProps> = ({ isOperating }) => {
  const state = isOperating ? 'active' : 'offline';

  return <Div variant="operational" className={state} />;
};

interface ContactWrapperProps extends Omit<Contact, 'icon'> {}

export const ContactWrapper: React.FC<ContactWrapperProps> = ({
  href,
  prelabel,
  label,
  responseTime,
  text = '',
  extraText = '',
  afterText = '',
  type,
}): JSX.Element => {
  const isChat = type === 'chat';
  const anchorColor = type === 'phone' ? 'main.2' : 'main.1';

  const anchorComp = (
    <Div display="flex">
      {prelabel && (
        <Div mr={1} my="auto" variant="helpContact">
          {prelabel}
        </Div>
      )}
      <Anchor
        style={{
          textDecoration: 'none',
          wordBreak: 'break-all',
        }}
        variant="helpContact"
        href={href || label}
        noHref={isChat}
        onClick={() => {
          if (isChat) {
            window.FrontChat?.('show');
          }
        }}
        type={type}
        color={anchorColor}
      >
        {label}
      </Anchor>
    </Div>
  );

  const subtitle = responseTime ? `Binnen: ${responseTime}` : text;

  return subtitle ? (
    <Flex flexDirection="column">
      {anchorComp}
      <P variant="captionRegular">{subtitle}</P>
      {afterText?.length > 0 && <P variant="captionRegular">{afterText}</P>}
      {extraText?.length > 0 && <P variant="captionImportant">{extraText}</P>}
    </Flex>
  ) : (
    anchorComp
  );
};

const Flex = (props) => <Div display="flex" {...props} />;
const Grid = (props) => <Div display="grid" {...props} />;

export const InnerHelp: React.FC<InnerHelpProps> = React.forwardRef<
  HTMLDivElement,
  InnerHelpProps
>(
  (
    {
      title,
      imageUrl,
      subtitle,
      contacts,
      imageProps,
      operation,
      refForModal,
      modals,
      showIcon,
      imageStyles,
      ...divProps
    },
    ref,
  ) => {
    const iconSize = 22;

    const contactLine = (
      {
        href,
        icon,
        label,
        prelabel,
        responseTime,
        extraText,
        text,
        afterText,
        type,
      }: Contact,
      key: number,
    ) => (
      <Flex key={`contact-${key}`} variant="contactItem">
        <Div mr={2}>
          <Icon
            display="flex"
            my="auto"
            name={icon}
            size={iconSize}
            variant="iconLink"
          />
        </Div>
        <ContactWrapper
          href={href}
          prelabel={prelabel}
          label={label}
          responseTime={responseTime}
          text={text}
          afterText={afterText}
          type={type}
          extraText={extraText}
        />
      </Flex>
    );

    const innerHelpTop = (
      <Div borderBottom="1px solid" borderColor="second.1">
        <Flex flexDirection="column">
          <Desktop>
            <Heading
              variant="H5"
              as="h5"
              color="main.2"
              mb={5}
              mt={0}
              textAlign={['center', 'left']}
            >
              {title}
            </Heading>
          </Desktop>

          {imageUrl && (
            <Div
              height="300px"
              backgroundImage={`url(${imageUrl})`}
              backgroundRepeat="no-repeat"
              backgroundPosition="center center"
              backgroundSize="cover"
              borderRadius={2}
              mb={6}
              {...imageStyles}
            />
          )}
          <Flex flexDirection="column">
            {modals?.faq && (
              <Flex mb={4} alignItems="center">
                <ModalWithAccordion
                  ref={refForModal}
                  showIcon={showIcon}
                  {...modals.faq}
                />
              </Flex>
            )}
            <Mobile>
              {modals?.howWeCompare && (
                <Flex mb={4} alignItems="center">
                  <ModalWithAccordion
                    ref={refForModal}
                    showIcon={showIcon}
                    {...modals.howWeCompare}
                  />
                </Flex>
              )}
            </Mobile>
          </Flex>
        </Flex>
      </Div>
    );

    const innerHelpBottom = (
      <Div pt={4}>
        <Flex flexDirection="column">
          <Heading
            variant="bodyImportant"
            as="h3"
            color="main.2"
            mb={3}
            mt={0}
            fontSize={[3, null, 5]}
          >
            {subtitle}
          </Heading>
          {operation && (
            <Grid
              pb={6}
              alignItems="center"
              gridTemplateColumns="7px auto"
              gridGap={2}
            >
              <Operational isOperating={operation.isOperating} />
              <P variant="footnoteRegular" fontSize={[2, null, 3]}>
                {operation.closingHoursMessage}
              </P>
            </Grid>
          )}

          {contacts.map((contact, key) => contactLine(contact, key))}
        </Flex>
      </Div>
    );

    return (
      <Div ref={ref} width={[null, '370px']} {...divProps}>
        {innerHelpTop}
        {innerHelpBottom}
      </Div>
    );
  },
);
export interface HelpProps extends InnerHelpProps {
  title: string;
  modals?: HelpModalsProps;
  showIcon?: boolean;
}

export const Help: React.FC<HelpProps> = ({
  title,
  imageUrl,
  subtitle,
  operation,
  contacts,
  modals,
  showIcon = false,
  ...rest
}) => {
  const [show, setShow] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (modalRef.current === null) {
      setShow(false);
    }
  };

  useEffect(() => {
    if (isMobile()) {
      if (show) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'visible';
      }
    }
  }, [show]);

  if (!contacts) return null;
  return (
    <>
      <Button
        onClick={() => setShow(!show)}
        ref={buttonRef}
        variant="transparent"
        display="flex"
        alignItems="center"
      >
        <Icon name="chat" mr={1} size={24} variant="iconLink" />
        <Desktop>
          {title}
          <Icon name="big-arrow-down" ml={1} size={16} variant="iconLink" />
        </Desktop>
      </Button>
      <Window
        showIcon={showIcon}
        onClose={handleClose}
        popoverProps={{
          arrow: true,
          attachTo: buttonRef.current,
          position: 'bottom-right',
          containerProps: { zIndex: 1 },
        }}
        show={show}
        title={title}
        type="popover"
      >
        <Div p={[null, 2]}>
          <InnerHelp
            refForModal={modalRef}
            title={title}
            imageUrl={imageUrl}
            subtitle={subtitle}
            operation={operation}
            contacts={contacts}
            modals={modals}
            showIcon={showIcon}
            {...rest}
          />
        </Div>
      </Window>
    </>
  );
};
